/**
 * Peer-to-peer voice.
 *
 * The chant the opponent hears is the player's real voice, not a synthesised
 * cue — that is what makes a long incantation feel dangerous. Signalling rides
 * the existing Colyseus room, so there is no second server to run.
 *
 * The ICE list comes from the game server rather than being hard-coded here,
 * because when a TURN key is configured it carries short-lived credentials that
 * have no business sitting in a public client bundle. Everything below still
 * works if that request fails; it just falls back to the public STUN pair and
 * the pairs that need a relay go quiet, exactly as they did before.
 */
import { C2S } from '@spell/network-protocol';
import { net, serverHttpUrl, type NetEvents } from './net';
import { mic } from '../voice/mic';

type Signal = NetEvents['rtcSignal'];

/** Used when the server cannot be reached, or has no TURN key configured. */
const FALLBACK_ICE_SERVERS: RTCIceServer[] = [
  { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
];

/** Resolved once per page load — the credentials outlive any single match. */
let icePromise: Promise<RTCIceServer[]> | null = null;

/**
 * Fetch the ICE list, once.
 *
 * Call this as early as the connection check so the answer is already in hand
 * by the time both duellists are known; `start()` awaits the same promise and
 * the round trip is not free — see the note there.
 */
export function prefetchIceServers(): Promise<RTCIceServer[]> {
  icePromise ??= (async () => {
    try {
      const res = await fetch(`${serverHttpUrl()}/api/ice`, {
        signal: AbortSignal.timeout(5000),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as { iceServers?: RTCIceServer[] };
      if (!body.iceServers?.length) throw new Error('empty ICE list');
      return body.iceServers;
    } catch {
      // Losing the relay costs some players their voice link. Losing the match
      // over it would be worse, so this path stays silent and keeps going.
      return FALLBACK_ICE_SERVERS;
    }
  })();
  return icePromise;
}

export interface VoiceLink {
  connected: boolean;
  /** Live level of the *incoming* voice, for the opponent's chant indicator. */
  remoteLevel: number;
  error: string | null;
}

export const voiceLink: VoiceLink = { connected: false, remoteLevel: 0, error: null };

class VoiceBridge {
  private pc: RTCPeerConnection | null = null;
  private audioEl: HTMLAudioElement | null = null;
  private analyser: AnalyserNode | null = null;
  private ctx: AudioContext | null = null;
  private raf = 0;
  private unsubscribe: (() => void) | null = null;
  private polite = false;
  private makingOffer = false;
  /** Bumped by every start and every stop, so an in-flight start can tell it lost. */
  private generation = 0;

  /**
   * @param initiator the host creates the offer; the guest answers.
   */
  async start(initiator: boolean) {
    this.stop();
    this.polite = !initiator;
    const generation = ++this.generation;

    /*
     * Subscribe before anything is awaited.
     *
     * Fetching ICE credentials puts a network round trip between this call and
     * a live RTCPeerConnection, and the other side may have finished its own
     * fetch first and already sent an offer. `net.events` does not buffer, so a
     * signal that arrives with no listener is gone — and a dropped offer leaves
     * the link half-open in precisely the way the session-id ordering in the
     * connection check was written to prevent. Queue what arrives early, drain
     * it once there is something to hand it to.
     */
    const queued: Signal[] = [];
    let deliver = (msg: Signal) => {
      queued.push(msg);
    };
    this.unsubscribe = net.events.on('rtcSignal', (msg) => deliver(msg));

    const iceServers = await prefetchIceServers();
    // A stop(), or a second start(), landed while the credentials were in flight.
    if (generation !== this.generation) return;

    const pc = new RTCPeerConnection({ iceServers });
    this.pc = pc;

    const stream = mic.mediaStream;
    if (stream) {
      for (const track of stream.getAudioTracks()) pc.addTrack(track, stream);
    } else {
      // Still negotiate so we can *receive* the opponent even without a mic.
      pc.addTransceiver('audio', { direction: 'recvonly' });
    }

    pc.ontrack = (event) => {
      const [remote] = event.streams;
      if (!remote) return;
      this.attachRemote(remote);
      voiceLink.connected = true;
    };

    pc.onicecandidate = (event) => {
      if (!event.candidate) return;
      net.send(C2S.RTC_SIGNAL, {
        kind: 'candidate',
        payload: JSON.stringify(event.candidate.toJSON()),
      });
    };

    pc.onconnectionstatechange = () => {
      voiceLink.connected = pc.connectionState === 'connected';
      if (pc.connectionState === 'failed') voiceLink.error = '음성 연결에 실패했습니다.';
    };

    pc.onnegotiationneeded = async () => {
      try {
        this.makingOffer = true;
        await pc.setLocalDescription();
        net.send(C2S.RTC_SIGNAL, {
          kind: 'offer',
          payload: JSON.stringify(pc.localDescription),
        });
      } catch (err) {
        voiceLink.error = err instanceof Error ? err.message : String(err);
      } finally {
        this.makingOffer = false;
      }
    };

    deliver = (msg) => void this.handleSignal(msg);
    for (const msg of queued) void this.handleSignal(msg);

    if (initiator && !stream) {
      // No mic means no negotiationneeded fires from a track; kick it manually.
      await pc.setLocalDescription();
      net.send(C2S.RTC_SIGNAL, { kind: 'offer', payload: JSON.stringify(pc.localDescription) });
    }
  }

  /** Perfect-negotiation pattern: both sides can offer, the polite peer yields. */
  private async handleSignal(msg: Signal) {
    if (!this.pc) return;
    try {
      if (msg.kind === 'candidate') {
        const candidate = JSON.parse(msg.payload) as RTCIceCandidateInit;
        await this.pc.addIceCandidate(candidate).catch(() => undefined);
        return;
      }
      const description = JSON.parse(msg.payload) as RTCSessionDescriptionInit;
      const offerCollision =
        description.type === 'offer' && (this.makingOffer || this.pc.signalingState !== 'stable');
      if (offerCollision && !this.polite) return;
      if (offerCollision)
        await this.pc.setLocalDescription({ type: 'rollback' } as RTCLocalSessionDescriptionInit);
      await this.pc.setRemoteDescription(description);
      if (description.type === 'offer') {
        await this.pc.setLocalDescription();
        net.send(C2S.RTC_SIGNAL, {
          kind: 'answer',
          payload: JSON.stringify(this.pc.localDescription),
        });
      }
    } catch (err) {
      voiceLink.error = err instanceof Error ? err.message : String(err);
    }
  }

  private attachRemote(stream: MediaStream) {
    if (!this.audioEl) {
      this.audioEl = document.createElement('audio');
      this.audioEl.autoplay = true;
      this.audioEl.style.display = 'none';
      document.body.appendChild(this.audioEl);
    }
    this.audioEl.srcObject = stream;
    void this.audioEl.play().catch(() => undefined);

    try {
      const Ctor = window.AudioContext;
      this.ctx = new Ctor();
      const source = this.ctx.createMediaStreamSource(stream);
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 512;
      source.connect(this.analyser);
      const buffer = new Float32Array(new ArrayBuffer(this.analyser.fftSize * 4));
      const measure = () => {
        if (!this.analyser) return;
        this.analyser.getFloatTimeDomainData(buffer);
        let sum = 0;
        for (let i = 0; i < buffer.length; i++) sum += buffer[i] * buffer[i];
        voiceLink.remoteLevel = Math.sqrt(sum / buffer.length);
        this.raf = requestAnimationFrame(measure);
      };
      measure();
    } catch {
      // Level metering is cosmetic; audio still plays through the element.
    }
  }

  stop() {
    this.generation++;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.pc?.close();
    this.pc = null;
    void this.ctx?.close();
    this.ctx = null;
    this.analyser = null;
    if (this.audioEl) {
      this.audioEl.srcObject = null;
      this.audioEl.remove();
      this.audioEl = null;
    }
    voiceLink.connected = false;
    voiceLink.remoteLevel = 0;
  }
}

export const voiceBridge = new VoiceBridge();
